import React, { useState, useEffect } from 'react';
import { useConfirm } from './ConfirmProvider';

/**
 * AgedReceivables Component
 * 
 * Visual aging report for outstanding invoices with interactive bar chart.
 * Features:
 * - 4 aging buckets: 0-30, 31-60, 61-90, 90+ days
 * - Color-coded bars (green, yellow, orange, red)
 * - Click buckets to filter invoice list
 * - Summary metrics and drill-down
 */
const AgedReceivables = ({ authAxios, showToast, invoices, clients }) => {
  const confirm = useConfirm();
  const [agingData, setAgingData] = useState({
    '0-30': { count: 0, amount: 0, invoices: [] },
    '31-60': { count: 0, amount: 0, invoices: [] },
    '61-90': { count: 0, amount: 0, invoices: [] },
    '90+': { count: 0, amount: 0, invoices: [] },
  });
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerSummary, setPartnerSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceOverrides, setInvoiceOverrides] = useState({});

  useEffect(() => {
    calculateAgingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  useEffect(() => {
    // When overrides change, recompute aging buckets.
    calculateAgingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceOverrides]);

  const calculateAgingData = () => {
    try {
      setLoading(true);
      const today = new Date();
      const buckets = {
        '0-30': { count: 0, amount: 0, invoices: [] },
        '31-60': { count: 0, amount: 0, invoices: [] },
        '61-90': { count: 0, amount: 0, invoices: [] },
        '90+': { count: 0, amount: 0, invoices: [] },
      };

      const mergedInvoices = (Array.isArray(invoices) ? invoices : []).map(inv => invoiceOverrides[inv.id] || inv);

      // Filter to unpaid/partially paid invoices only
      const outstandingInvoices = mergedInvoices.filter(inv => {
        const balance = parseFloat(inv.balance_due || 0);
        return balance > 0 && inv.status !== 'Paid';
      });

      // Partner summary (for accountability)
      const partnerMap = {};
      outstandingInvoices.forEach(inv => {
        const balance = parseFloat(inv.balance_due || 0);
        const partnerName = inv.responsible_partner_name || 'Unassigned';
        if (!partnerMap[partnerName]) {
          partnerMap[partnerName] = { partner: partnerName, count: 0, amount: 0 };
        }
        partnerMap[partnerName].count += 1;
        partnerMap[partnerName].amount += balance;
      });
      setPartnerSummary(Object.values(partnerMap).sort((a, b) => b.amount - a.amount));

      outstandingInvoices.forEach(invoice => {
        const dueDate = new Date(invoice.due_date);
        const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
        const balance = parseFloat(invoice.balance_due || 0);

        let bucket;
        if (daysOverdue < 0) {
          bucket = '0-30'; // Not yet overdue
        } else if (daysOverdue <= 30) {
          bucket = '0-30';
        } else if (daysOverdue <= 60) {
          bucket = '31-60';
        } else if (daysOverdue <= 90) {
          bucket = '61-90';
        } else {
          bucket = '90+';
        }

        buckets[bucket].count++;
        buckets[bucket].amount += balance;
        buckets[bucket].invoices.push({
          ...invoice,
          daysOverdue: Math.max(0, daysOverdue),
        });
      });

      setAgingData(buckets);
    } catch (err) {
      console.error('Failed to calculate aging data:', err);
      showToast('Failed to calculate aging data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTrust = async (invoice) => {
    try {
      const trust = parseFloat(invoice.available_trust_balance || 0);
      const balance = parseFloat(invoice.balance_due || 0);
      const amount = Math.min(trust, balance);
      if (!amount || amount <= 0) {
        showToast('No trust funds available to apply', 'error');
        return;
      }

      const ok = await confirm({
        title: 'Apply trust funds?',
        message: `Apply P${amount.toFixed(2)} from Trust to Invoice #${invoice.invoice_number}?`,
        confirmText: 'Apply',
        cancelText: 'Cancel',
        variant: 'warning',
      });
      if (!ok) return;

      const resp = await authAxios.post(`invoices/${invoice.id}/apply_trust/`, { amount });
      const updated = resp.data;

      setInvoiceOverrides(prev => ({ ...prev, [updated.id]: updated }));
      showToast('Trust applied to invoice', 'success');
    } catch (err) {
      console.error('Failed to apply trust:', err);
      const detail = err?.response?.data?.detail || err?.response?.data?.error || 'Failed to apply trust';
      showToast(detail, 'error');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return `Client ${clientId}`;
    return client.first_name ? `${client.first_name} ${client.last_name}` : client.organization_name;
  };

  const totalOutstanding = Object.values(agingData).reduce((sum, bucket) => sum + bucket.amount, 0);
  const maxBucketAmount = Math.max(...Object.values(agingData).map(b => b.amount), 1);
  const maxPartnerAmount = Math.max(...partnerSummary.map(p => p.amount), 1);

  const bucketColors = {
    '0-30': { bg: 'bg-green-500', hover: 'hover:bg-green-600', text: 'text-green-700', border: 'border-green-500' },
    '31-60': { bg: 'bg-yellow-500', hover: 'hover:bg-yellow-600', text: 'text-yellow-700', border: 'border-yellow-500' },
    '61-90': { bg: 'bg-orange-500', hover: 'hover:bg-orange-600', text: 'text-orange-700', border: 'border-orange-500' },
    '90+': { bg: 'bg-red-500', hover: 'hover:bg-red-600', text: 'text-red-700', border: 'border-red-500' },
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Aged Receivables</h2>
        <p className="text-gray-600">Outstanding invoices by aging bucket</p>
      </div>

      {/* Summary Card */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-600">Total Outstanding</h3>
            <p className="text-4xl font-bold text-indigo-600 mt-2">P{totalOutstanding.toFixed(2)}</p>
          </div>
          <div className="grid grid-cols-2 gap-4 text-right">
            <div>
              <p className="text-sm text-gray-600">0-30 Days</p>
              <p className="text-xl font-semibold text-green-600">{agingData['0-30'].count}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">31-60 Days</p>
              <p className="text-xl font-semibold text-yellow-600">{agingData['31-60'].count}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">61-90 Days</p>
              <p className="text-xl font-semibold text-orange-600">{agingData['61-90'].count}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">90+ Days</p>
              <p className="text-xl font-semibold text-red-600">{agingData['90+'].count}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Aging Bar Chart */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Aging Distribution</h3>
        <div className="space-y-4">
          {Object.entries(agingData).map(([bucket, data]) => {
            const percentage = totalOutstanding > 0 ? (data.amount / totalOutstanding) * 100 : 0;
            const barWidth = totalOutstanding > 0 ? (data.amount / maxBucketAmount) * 100 : 0;
            const colors = bucketColors[bucket];
            const isSelected = selectedBucket === bucket;

            return (
              <div key={bucket} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700">{bucket} Days</span>
                  <span className="text-gray-600">
                    {data.count} invoice{data.count !== 1 ? 's' : ''} • P{data.amount.toFixed(2)} ({percentage.toFixed(1)}%)
                  </span>
                </div>
                <div 
                  className={`relative h-12 bg-gray-100 rounded-lg cursor-pointer transition-all ${isSelected ? `ring-2 ${colors.border}` : ''}`}
                  onClick={() => setSelectedBucket(isSelected ? null : bucket)}
                >
                  <div 
                    className={`h-full ${colors.bg} ${colors.hover} rounded-lg transition-all flex items-center justify-center text-white font-semibold`}
                    style={{ width: `${barWidth}%` }}
                  >
                    {data.amount > 0 && (
                      <span className="text-sm">P{data.amount.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Partner Accountability Summary */}
      {totalOutstanding > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Outstanding by Responsible Partner</h3>
            {selectedPartner && (
              <button
                onClick={() => setSelectedPartner(null)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear Partner Filter
              </button>
            )}
          </div>

          {partnerSummary.length === 0 ? (
            <p className="text-gray-500">No outstanding invoices.</p>
          ) : (
            <div className="space-y-3">
              {partnerSummary.map(p => {
                const pct = totalOutstanding > 0 ? (p.amount / totalOutstanding) * 100 : 0;
                const barWidth = maxPartnerAmount > 0 ? (p.amount / maxPartnerAmount) * 100 : 0;
                const isSelected = selectedPartner === p.partner;
                return (
                  <div key={p.partner} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <button
                        className={`font-medium text-left ${isSelected ? 'text-indigo-700' : 'text-gray-700'} hover:text-indigo-700`}
                        onClick={() => setSelectedPartner(isSelected ? null : p.partner)}
                        type="button"
                      >
                        {p.partner}
                      </button>
                      <span className="text-gray-600">
                        {p.count} invoice{p.count !== 1 ? 's' : ''} • P{p.amount.toFixed(2)} ({pct.toFixed(1)}%)
                      </span>
                    </div>
                    <div className={`relative h-10 bg-gray-100 rounded-lg ${isSelected ? 'ring-2 ring-indigo-400' : ''}`}>
                      <div
                        className="h-full bg-indigo-500 rounded-lg"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Filtered Invoice List */}
      {selectedBucket && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              {selectedBucket} Days Overdue
              {selectedPartner ? ` • ${selectedPartner}` : ''}
            </h3>
            <button
              onClick={() => {
                setSelectedBucket(null);
                setSelectedPartner(null);
              }}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear Filter
            </button>
          </div>

          {(() => {
            const filteredInvoices = agingData[selectedBucket].invoices.filter(inv => {
              if (!selectedPartner) return true;
              const partnerName = inv.responsible_partner_name || 'Unassigned';
              return partnerName === selectedPartner;
            });

            if (filteredInvoices.length === 0) {
              return (
                <div className="p-8 text-center text-gray-500">
                  No invoices match this filter
                </div>
              );
            }

            return (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Partner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Days Overdue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Balance Due
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Available Trust
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fees
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Disb.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map(invoice => {
                      const trust = parseFloat(invoice.available_trust_balance || 0);
                      const balance = parseFloat(invoice.balance_due || 0);
                      const canOffset = trust >= balance && balance > 0;
                      const canApplyAny = trust > 0 && balance > 0;
                      return (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">#{invoice.invoice_number}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{getClientName(invoice.client)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{invoice.responsible_partner_name || 'Unassigned'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{invoice.due_date}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-medium ${bucketColors[selectedBucket].text}`}>
                              {invoice.daysOverdue} days
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">
                              P{balance.toFixed(2)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-semibold ${canOffset ? 'text-green-700' : 'text-gray-900'}`}>
                              P{trust.toFixed(2)}
                            </div>
                            {canOffset && (
                              <div className="text-xs text-green-700">Can offset from trust</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              P{parseFloat(invoice.professional_fees_total || 0).toFixed(2)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              P{parseFloat(invoice.disbursements_total || 0).toFixed(2)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleApplyTrust(invoice)}
                              disabled={!canApplyAny}
                              className={`px-3 py-1 rounded-md text-sm font-medium shadow-sm ${canApplyAny ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                              title={canApplyAny ? 'Apply trust funds to this invoice' : 'No trust funds available'}
                            >
                              Apply Trust
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {!selectedBucket && totalOutstanding === 0 && (
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <svg className="mx-auto h-12 w-12 text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">All Caught Up!</h3>
          <p className="text-gray-600">No outstanding invoices at this time</p>
        </div>
      )}
    </div>
  );
};

export default AgedReceivables;
